import { personRef } from "@/content/author";
import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks } from "@/lib/seo";
import { PageIntro, PageSection, MoreLink } from "@/components/site/page";

const URL = "https://akillisurdurulebilirlik.com/yazilar/surdurulebilirlik-nedir-rehberi";
const TITLE = "Sürdürülebilirlik Nedir? — Tanım, Tarih ve Eleştirel Bir Rehber";
const DESC =
  "Sürdürülebilirlik nedir, nereden geliyor, neden çoğu zaman yanlış anlaşılıyor? Üç sütun, kısa tarih, yaygın yanılgılar ve yapay zekâ çağında insan-merkezli bir okuma.";
const PUBLISHED = "2026-06-08";

const FAQ = [
  {
    q: "Sürdürülebilirlik nedir, kısaca?",
    a: "Sürdürülebilirlik, bugünün ihtiyaçlarını karşılarken gelecek kuşakların kendi ihtiyaçlarını karşılama kapasitesini zayıflatmama ilkesidir. Üç sütun üzerine kurulur: çevresel, ekonomik ve sosyal. Pratikte bu, doğal sistemlerin yenilenme hızını aşmayan bir üretim ve tüketim düzeni anlamına gelir.",
  },
  {
    q: "Sürdürülebilirliğin üç sütunu nedir?",
    a: "Çevre (ekosistemlerin taşıma kapasitesi), ekonomi (uzun vadede ayakta durabilen değer üretimi) ve toplum (eşitlik, sağlık, kültürel süreklilik). Üçü aynı anda çözülmediği sürece denge bozulur; tek başına 'yeşil' bir ekonomi de, sosyal adalet olmadan sürdürülebilirlik sayılmaz.",
  },
  {
    q: "Sürdürülebilirlik ile ESG aynı şey mi?",
    a: "Hayır. ESG (Environmental, Social, Governance) şirketlerin sürdürülebilirlik performansını ölçen bir raporlama çerçevesidir. Sürdürülebilirlik bir ilke; ESG ise bir muhasebe dilidir. ESG raporu olan bir şirket otomatik olarak sürdürülebilir değildir.",
  },
  {
    q: "Sürdürülebilirlik ile yeşil aklama (greenwashing) farkı nedir?",
    a: "Greenwashing, sürdürülebilirlik söylemini bir pazarlama katmanı olarak kullanmak ama altyapıyı değiştirmemektir. Karbon dengeleme sertifikaları, 'doğa dostu' etiketler, AI'ın 'iklim için' kullanıldığı iddiaları çoğu zaman bu kategoriye girer.",
  },
  {
    q: "Bireysel olarak sürdürülebilirlik için ne yapabilirim?",
    a: "Tüketim hacmini düşürmek (yeni satın almadan önce 'gerekli mi?' diye sormak), enerji ve gıda kararlarını yerelleştirmek, dijital ayak izini azaltmak ve daha önemlisi: sistemsel değişimi talep eden kolektif yapılarda yer almak. Sürdürülebilirlik kişisel bir erdem değil, kolektif bir mimaridir.",
  },
];

export const Route = createFileRoute("/yazilar/surdurulebilirlik-nedir-rehberi")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "sürdürülebilirlik nedir, sürdürülebilirlik tanımı, sürdürülebilirlik tarihi, üç sütun, çevresel sürdürülebilirlik, ekonomik sürdürülebilirlik, sosyal sürdürülebilirlik, ESG, greenwashing, yapay zeka ve sürdürülebilirlik",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [
      { rel: "canonical", href: URL },
      ...hreflangLinks("/yazilar/surdurulebilirlik-nedir-rehberi", "/ideas/what-is-sustainability-a-critique"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESC,
          inLanguage: "tr-TR",
          datePublished: PUBLISHED,
          dateModified: PUBLISHED,
          author: personRef(),
          publisher: {
            "@type": "Organization",
            name: "Akıllı Sürdürülebilirlik",
            url: "https://akillisurdurulebilirlik.com",
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": URL },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://akillisurdurulebilirlik.com/" },
            { "@type": "ListItem", position: 2, name: "Yazılar", item: "https://akillisurdurulebilirlik.com/yazilar" },
            { "@type": "ListItem", position: 3, name: "Sürdürülebilirlik Nedir?", item: URL },
          ],
        }),
      },
    ],
  }),
  component: SurdurulebilirlikNedirPage,
});

function SurdurulebilirlikNedirPage() {
  return (
    <>
      <PageIntro
        crumbs={[{ label: "Yazılar", to: "/yazilar" }, { label: "Sürdürülebilirlik Nedir?" }]}
        kicker="Rehber"
        title="Sürdürülebilirlik nedir?"
        lede={
          <>
            Tanımı herkes biliyor sanıyor. Oysa kelimenin altındaki düşünme biçimi
          son elli yılda en az üç kez yer değiştirdi — ve bugün, yapay zekâ çağında
          bir kez daha yer değiştiriyor. Bu rehber, sürdürülebilirliğin ne olduğunu,
          nereden geldiğini ve neden çoğu zaman yanlış anlaşıldığını anlatıyor.
          </>
        }
      />
      <PageSection>
        <article className="max-w-3xl">

      <nav className="mb-12 border border-ink/10 p-5 md:p-6 bg-secondary" aria-label="İçindekiler">
        <h2 className="text-[10px] uppercase tracking-[0.25em] font-bold text-ink/60 mb-3">
          İçindekiler
        </h2>
        <ol className="space-y-1.5 text-sm">
          <li><a href="#tanim" className="hover:text-theme-tech">1. Sürdürülebilirliğin kısa tanımı</a></li>
          <li><a href="#tarih" className="hover:text-theme-tech">2. Nereden geldi: 1972'den bugüne</a></li>
          <li><a href="#uc-sutun" className="hover:text-theme-tech">3. Üç sütun: çevre, ekonomi, toplum</a></li>
          <li><a href="#yanilgilar" className="hover:text-theme-tech">4. En yaygın altı yanılgı</a></li>
          <li><a href="#esg" className="hover:text-theme-tech">5. ESG, greenwashing ve ölçüm sorunu</a></li>
          <li><a href="#ai-ve-iklim" className="hover:text-theme-tech">6. Yapay zekâ çağında sürdürülebilirlik</a></li>
          <li><a href="#insan" className="hover:text-theme-tech">7. Eksik sütun: insanın sürdürülebilirliği</a></li>
          <li><a href="#ne-yapmali" className="hover:text-theme-tech">8. Bireysel ve kolektif: ne yapmalı?</a></li>
          <li><a href="#sss" className="hover:text-theme-tech">9. Sıkça sorulanlar</a></li>
        </ol>
      </nav>

      <div className="prose-content space-y-6 text-base md:text-lg leading-[1.75] text-ink/85">
        <section id="tanim" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            1. Sürdürülebilirliğin kısa tanımı
          </h2>
          <p>
            Sürdürülebilirlik, Birleşmiş Milletler Brundtland Komisyonu'nun
            1987'deki <em>Our Common Future</em> raporunda yapılan tanımla
            yerleşti: <strong>bugünün ihtiyaçlarını, gelecek kuşakların kendi
            ihtiyaçlarını karşılama kapasitesini zayıflatmadan karşılamak</strong>.
            Cümle basit görünür. Ama içinde üç ağır kavram saklıdır: ihtiyaç,
            kapasite ve zaman.
          </p>
          <p>
            <em>İhtiyaç</em>, kim için tanımlanır? Küresel Kuzey'in tüketim
            standartları mı, Küresel Güney'in temel yaşam koşulları mı?{" "}
            <em>Kapasite</em>, ölçülebilir mi — bir nehrin, bir toprak parçasının,
            bir atmosferin sınırı tam olarak nerede? <em>Zaman</em>, kaç kuşak
            sonrasını kastediyoruz — üç, on, yüz? Sürdürülebilirlik tanımı bu üç
            sorunun cevabıyla genişler ya da daralır.
          </p>
        </section>

        <section id="tarih" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            2. Nereden geldi: 1972'den bugüne
          </h2>
          <p>
            Kavramın modern kullanımı, 1972'de Roma Kulübü'nün yayımladığı{" "}
            <em>The Limits to Growth</em> raporuna dayanır. O rapor ilk kez,
            sınırsız büyümenin sınırlı bir gezegende matematiksel olarak mümkün
            olmadığını gösterdi. Aynı yıl Stockholm'de toplanan ilk BM Çevre
            Konferansı, çevrenin uluslararası bir mesele olduğunu tescil etti.
          </p>
          <p>
            1987'de Brundtland tanımı, 1992'de Rio Zirvesi'nde Gündem 21,
            2000'de Binyıl Kalkınma Hedefleri, 2015'te Sürdürülebilir Kalkınma
            Hedefleri (SKH) ve Paris İklim Anlaşması. Her on yılda kelimenin
            kapsamı bir kat daha genişledi: önce çevre, sonra ekonomi, sonra
            sosyal eşitlik, son olarak iklim adaleti ve dijital dönüşüm.
          </p>
          <p>
            Bu genişleme bir başarı mı, yoksa kavramın iç tutarlılığını
            yitirmesinin işareti mi — bu rehberin sonuna kadar açık bırakacağımız
            soru bu.
          </p>
        </section>

        <section id="uc-sutun" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            3. Üç sütun: çevre, ekonomi, toplum
          </h2>
          <p>
            Standart model sürdürülebilirliği üç iç içe daire olarak gösterir.
            Üçü de ayakta olmadan denge kurulmaz:
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li>
              <strong>Çevresel sürdürülebilirlik:</strong> Ekosistemlerin
              yenilenme hızını aşmayan kaynak kullanımı. Su, toprak, hava,
              biyoçeşitlik. Bir ormanı, kesim hızı yeniden ağaçlanma hızını
              geçmediği sürece "sürdürülebilir" işletiyorsunuz demektir.
            </li>
            <li>
              <strong>Ekonomik sürdürülebilirlik:</strong> Uzun vadede ayakta
              durabilen, dışsal maliyetleri başkasına yıkmayan değer üretimi.
              Kârın bir kısmının topluma ve ekosisteme geri akması.
            </li>
            <li>
              <strong>Sosyal sürdürülebilirlik:</strong> Eşitlik, sağlık,
              eğitim, kültürel ve kuşaklar arası süreklilik. Bir teknolojinin ya
              da politikanın sadece şimdiki seçmeni değil, henüz doğmamış olanı
              da hesaba katması.
            </li>
          </ul>
          <p>
            Modelin zayıf noktası, üç sütunu sanki ayrı sistemlermiş gibi
            göstermesidir. Oysa pratikte birinin maliyeti çoğu zaman diğerinin
            "kazancı" olarak görünür. "Yeşil" bir veri merkezi, soğutma için bir
            kasabanın suyunu çekiyorsa, çevresel kazanç sosyal sürdürülebilirliği
            zedeler.
          </p>
        </section>

        <section id="yanilgilar" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            4. En yaygın altı yanılgı
          </h2>
          <ol className="list-decimal pl-6 space-y-3">
            <li>
              <strong>"Sürdürülebilirlik = çevrecilik."</strong> Değil. Çevrecilik
              sürdürülebilirliğin bir alt kümesi. Sürdürülebilirlik aynı zamanda
              kurumsal yönetişim, finansal süreklilik ve toplumsal güven meselesidir.
            </li>
            <li>
              <strong>"Geri dönüşüm yeterli."</strong> Geri dönüşüm hiyerarşinin
              en alt basamağıdır. Önce azalt, sonra yeniden kullan, sonra dönüştür.
              Dönüşüm hattına dayanan bir ekonomi, üretim hacmini düşürmek zorunda
              kalmadığı için aslında tüketimi normalleştirir.
            </li>
            <li>
              <strong>"Karbon ayak izini sıfırlamak yeterli."</strong> Karbon
              tek değişken değil. Su ayak izi, toprak kaybı, biyoçeşitlik
              erozyonu, mineral tüketimi — hepsi ayrı muhasebe ister.
            </li>
            <li>
              <strong>"Bireysel davranış sistemi değiştirir."</strong> Değiştirmez —
              tek başına. Sistemsel kararlar (enerji altyapısı, vergi politikası,
              şehir planlaması) bireysel toplamın yüzlerce katı etki yapar.
              Bireysel davranış kolektif baskının provasıdır, alternatifi değil.
            </li>
            <li>
              <strong>"Teknoloji çözer."</strong> Teknoloji araçtır. Hangi
              soruna, kimin için, hangi maliyetle uygulandığına bağlı. Verimliliği
              artıran her teknoloji aynı zamanda tüketimi artırabilir — Jevons
              paradoksu.
            </li>
            <li>
              <strong>"Sürdürülebilirlik bir hedef."</strong> Daha çok bir
              <em> denge durumu</em>. Hedef gibi davrandığınızda, ona ulaştığınızı
              ilan edip durabileceğinizi sanırsınız. Oysa denge, sürekli
              yeniden kurulması gereken bir şeydir.
            </li>
          </ol>
        </section>

        <section id="esg" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            5. ESG, greenwashing ve ölçüm sorunu
          </h2>
          <p>
            ESG (Environmental, Social, Governance) şirketlerin sürdürülebilirlik
            performansını ölçen bir raporlama çerçevesidir. İyi tasarlanmış bir
            ESG sistemi sermaye akışını yönlendirir, kötü tasarlanmış bir tanesi
            ise yalnızca bir pazarlama aracıdır.
          </p>
          <p>
            <strong>Greenwashing</strong> tam burada başlar: bir şirket, gerçek
            altyapısını değiştirmeden sürdürülebilirlik dilini benimseyebilir.
            Karbon dengeleme sertifikaları satın alır, "doğa dostu" etiket
            tasarlar, AI'ı "iklim için" kullandığını ilan eder — ama üretim
            hacmi, tedarik zinciri ve enerji karması olduğu yerde kalır.
          </p>
          <p>
            Ölçümün kendisi bir politika kararıdır. Hangi metriği seçtiğiniz,
            hangi sonuca varacağınızı belirler. Bu yüzden sürdürülebilirlik
            okuryazarlığı, sayılara değil <em>sayıların arkasındaki tanımlara</em>{" "}
            bakmayı gerektirir.
          </p>
        </section>

        <section id="ai-ve-iklim" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            6. Yapay zekâ çağında sürdürülebilirlik
          </h2>
          <p>
            Yapay zekânın iklim tartışmasındaki yeri çift uçludur. Bir tarafta
            optimizasyon vaadi: daha verimli şebeke, daha az atık, daha iyi
            tahmin. Öbür tarafta ham gerçek: büyük model eğitimi gigavatlarca
            elektrik, milyonlarca litre soğutma suyu ve devasa mineral
            tüketimi demektir.
          </p>
          <p>
            "AI iklim için iyidir" cümlesi, sayılarla desteklenmediği sürece
            bir greenwashing varyantıdır. Doğru soru şudur: <em>hangi modeli,
            kim için, hangi enerji karmasıyla çalıştırıyoruz?</em>
          </p>
          <p>
            Bu noktada sürdürülebilirlik kavramı bir kez daha genişlemek
            zorunda. Karbon, su ve mineralin yanına dördüncü bir değişken
            ekleniyor: <strong>dikkat ve epistemik altyapı</strong>. Çünkü
            insanlığın bir karar verme kapasitesi varsa, onu da "tüketilebilir
            bir kaynak" olarak görmeye başladığımız anda sürdürülemez hale
            geliyoruz. Daha derin bir okuma için:{" "}
            <Link to="/yapay-zeka-toplum" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
              Yapay Zekâ ve Toplum
            </Link>.
          </p>
        </section>

        <section id="insan" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            7. Eksik sütun: insanın sürdürülebilirliği
          </h2>
          <p>
            Üç sütun modeli, bir kuşaktan önce yazıldı. Bugün, dördüncü bir
            sütuna ihtiyacımız var: <strong>insanın kendi yargı, dikkat ve
            anlam üretme kapasitesinin sürdürülebilirliği</strong>.
          </p>
          <p>
            Bu sitenin merkezindeki tez şudur: iklim krizi aslında bir{" "}
            <Link to="/kitaplar/iklim-degil-insanlik-krizi" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
              insanlık krizinin
            </Link>{" "}
            yan ürünü. Karar veremeyen, ortak gerçekliği yitiren, dikkati
            sermayeleştirilen bir tür, gezegeni sürdürülebilir biçimde
            yönetemez. Dolayısıyla "yeşil enerji" ve "AI verimliliği"
            tartışmaları, ancak bu daha derin sütun ayakta kaldığı sürece
            anlamlıdır.
          </p>
        </section>

        <section id="ne-yapmali" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            8. Bireysel ve kolektif: ne yapmalı?
          </h2>
          <p>
            Sürdürülebilirlik kişisel bir erdem değil, kolektif bir mimaridir.
            Yine de bireysel kararlar bu mimarinin tuğlalarıdır. Üç katmanda
            düşünmek faydalı:
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li>
              <strong>Hane düzeyi:</strong> Tüketim hacmini düşürmek, enerji ve
              gıda kararlarını yerelleştirmek, dijital ayak izini sadeleştirmek.
            </li>
            <li>
              <strong>Kurumsal düzey:</strong> Çalıştığınız ya da yönettiğiniz
              kurumda satın alma, enerji ve veri kararlarına müdahil olmak.
              Sürdürülebilirlik raporlarının arkasındaki metodolojiyi sorgulamak.
            </li>
            <li>
              <strong>Politik düzey:</strong> Sistemsel değişim isteyen sivil
              yapılarda yer almak. Oy, dilekçe ve kamusal söz dahil. Çünkü altyapı
              kararları her zaman bireysel toplamın önündedir.
            </li>
          </ul>
          <p>
            Hangi tarafından girmek isterseniz, bir başlangıç noktası için{" "}
            <Link to="/baslangic" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
              Buradan Başla
            </Link>{" "}
            sayfasındaki dört temalı okuma yolunu öneririm.
          </p>
        </section>

        <section id="sss" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            9. Sıkça sorulanlar
          </h2>
          <div className="space-y-6 mt-6">
            {FAQ.map((f) => (
              <div key={f.q}>
                <h3 className="font-serif text-lg md:text-xl text-ink mb-2">{f.q}</h3>
                <p className="text-ink/80">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 border-t border-ink/10 pt-10">
          <h2 className="font-serif text-2xl tracking-tight mb-4 text-ink">Devamı için</h2>
          <ul className="space-y-3 text-base">
            <li>
              →{" "}
              <Link to="/surdurulebilirlik" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
                Sürdürülebilirlik Eleştirisi
              </Link>{" "}
              — kavramın nerede kötüye kullanıldığına dair yazılar.
            </li>
            <li>
              →{" "}
              <Link to="/iklim-sistemleri" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
                İklim Sistemleri
              </Link>{" "}
              — gezegen ölçeğinde geri besleme döngüleri.
            </li>
            <li>
              →{" "}
              <Link to="/insan-ve-gelecek" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
                İnsan ve Gelecek
              </Link>{" "}
              — dördüncü sütun: insanın sürdürülebilirliği.
            </li>
          </ul>
        </section>
      </div>
        </article>
      </PageSection>
    </>
  );
}
